// uploadStore.js
// マニュアル動画アップロードの進行状況を Pinia store で管理する。
// コンポーネントの mount/unmount に依存せず、ページ遷移しても進行状況が見える。
//
// 背景:
// 旧実装では ManualList.vue の local ref (uploading / uploadProgress) を使っていたが、
// ユーザーが別ページに移動すると component が unmount されて progress bar が消え、
// 「失敗した」と勘違いして重複アップロードされる問題があった。
//
// 設計:
// - 各ファイルごとに upload エントリを作成 (id / name / size / progress / status)
// - status: 'uploading' | 'completed' | 'failed' | 'cancelled'
// - 完了/失敗後も短時間 store に残し、UI でユーザーが結果を確認できるようにする
// - abortController も store に持って、ページ遷移後でもキャンセル可能に
import { defineStore } from "pinia";
import { ref, computed } from "vue";

let nextId = 1;

export const useUploadStore = defineStore("upload", () => {
  // 全エントリ (uploading / completed / failed / cancelled)
  const uploads = ref([]);

  function startUpload({ name, size }) {
    const id = `upload-${nextId++}`;
    const abortController = new AbortController();
    uploads.value.push({
      id,
      name,
      size: size || 0,
      progress: 0,
      status: "uploading",
      errorMessage: null,
      abortController,
      startedAt: Date.now(),
    });
    return { id, signal: abortController.signal };
  }

  function updateProgress(id, percent) {
    const u = uploads.value.find((x) => x.id === id);
    if (u) u.progress = Math.min(100, Math.max(0, percent));
  }

  function completeUpload(id) {
    const u = uploads.value.find((x) => x.id === id);
    if (u) {
      u.status = "completed";
      u.progress = 100;
    }
  }

  function failUpload(id, errorMessage) {
    const u = uploads.value.find((x) => x.id === id);
    if (u) {
      u.status = "failed";
      u.errorMessage = errorMessage || "アップロードに失敗しました";
    }
  }

  function cancelUpload(id) {
    const u = uploads.value.find((x) => x.id === id);
    if (u && u.abortController) {
      try {
        u.abortController.abort();
      } catch {
        // already aborted, ignore
      }
      u.status = "cancelled";
    }
  }

  // 完了/失敗/キャンセルエントリを store から除去
  function removeUpload(id) {
    const idx = uploads.value.findIndex((x) => x.id === id);
    if (idx >= 0) uploads.value.splice(idx, 1);
  }

  // 完了/失敗/キャンセル済みのエントリを一括 cleanup (UI の「全部閉じる」用)
  function clearFinished() {
    uploads.value = uploads.value.filter((u) => u.status === "uploading");
  }

  // 進行中のみ
  const activeUploads = computed(() =>
    uploads.value.filter((u) => u.status === "uploading")
  );
  const hasActiveUploads = computed(() => activeUploads.value.length > 0);

  // 完了/失敗を含む全件 (UI 表示判定用)
  const hasAnyUploads = computed(() => uploads.value.length > 0);

  // 進行中の平均 progress
  const overallProgress = computed(() => {
    if (!activeUploads.value.length) return 0;
    const sum = activeUploads.value.reduce((s, u) => s + u.progress, 0);
    return sum / activeUploads.value.length;
  });

  return {
    uploads,
    activeUploads,
    hasActiveUploads,
    hasAnyUploads,
    overallProgress,
    startUpload,
    updateProgress,
    completeUpload,
    failUpload,
    cancelUpload,
    removeUpload,
    clearFinished,
  };
});
